import React from 'react';
import ReactDOM from 'react-dom';

import PenaltiesFilters from './components/filters';
import PenaltiesTable from './components/table';
import getFilteredData from './services/getFilteredData';
import sortRowsByField from './services/sortRowsByField';
import getSearchParams from './services/getSearchParams';

import loader from '../../../static/images/spinner-2x.gif';

/**
 * Hospital penalties app component.
 */
const appElement = document.getElementById('hospital-penalties-interactive');
const penaltyType = appElement.getAttribute('data-penalty');

class HospitalPenalties extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onFilter = this.onFilter.bind(this);
    this.onResetFilters = this.onResetFilters.bind(this);
    this.handleTableSortClick =
      this.handleTableSortClick.bind(this);
    // Search params for defaulting the table
    const searchParams = getSearchParams(window.location.search, penaltyType); // phpcs:ignore WordPressVIPMinimum.JS.Window.location
    this.state = {
      activePenalty: searchParams.penalty ?
        searchParams.penalty : 'hac',
      activeSearch: null,
      activeState: searchParams.state,
      data: [],
      loading: true,
      tableData: [],
      states: [],
      sortedColumn: searchParams.year,
      sortOrder: 'DESC',
      years: [],
    };
  }

  componentDidMount() {
    this.getData();
  }

  /**
   * Handles filtering of data table
   * @param {string} type - the filter type (penalty, search, state)
   * @param {string|number} value - the string or parameter by which to filter
   */
  onFilter(type, value) {
    const filter = ('all' === value || '' === value) ? undefined : value;
    this.setState((prevState) => ({
      [type]: filter,
      tableData: this.filterAndSort(
        prevState.data,
        ('activePenalty' === type) ? filter : prevState.activePenalty,
        prevState.sortedColumn,
        ('activeSearch' === type) ? filter : prevState.activeSearch,
        ('activeState' === type) ? filter : prevState.activeState,
        prevState.sortOrder
      ),
    }));
  }

  /**
   * Handles resetting data table filters and sorting
   */
  onResetFilters() {
    this.setState((prevState) => ({
      activeState: null,
      activeSearch: null,
      loading: false,
      sortedColumn: null,
      sortOrder: 'DESC',
      tableData: this.filterAndSort(
        prevState.data,
        prevState.activePenalty,
        null,
        null,
        null,
        'DESC'
      ),
    }));
  }

  /**
   * Gets application data from endpoints
   */
  getData() {
    Promise.all([
      // Readmissions
      fetch('/wp-json/khn/v1/data-source/readmissions')
        .then((response) => response.json()),
      // HACs
      fetch('/wp-json/khn/v1/data-source/hacs')
        .then((response) => response.json()),
    ]).then((data) => this.formatData(data));
  }
  /**
   * Formats, defaults, and assigns data to state
   * @param {Array} - data to be formatted
   */
  formatData(data) {
    const readmissions = JSON.parse(data[0]);
    const hacs = JSON.parse(data[1]);

    // Get unique list of years by investigating both data sources (HACs and Readmissions).
    const hacYears = Array.from(
      new Set(hacs[0].dataPoints.map((record) => record.year)));
    const readmissionYears = Array.from(
      new Set(readmissions[0].dataPoints.map((record) => record.year)));

    // Create an object with all records, since hospitals
    // can be in one penalty list and not the other
    const penaltyMap = {};
    readmissions.forEach((record) => {
      if ('' !== record.id) { // dirty data.
        penaltyMap[record.id] = record;
      }
    });

    // Add HACs
    hacs.forEach((record) => {
      // Hospital doesn't exist
      if (undefined === penaltyMap[record.id]) {
        if ('' !== record.id) { // dirty data.
          penaltyMap[record.id] = record;
        }
      } else { // exists; append its data to the dataPoints array
        const hosp = penaltyMap[record.id];
        hosp.dataPoints.push(...record.dataPoints);
      }
    });

    // No need for keys after this
    const concatenatedData = Object.values(penaltyMap);

    // Get a list of all states within the dataset
    const allStates = Array.from(
      new Set(
        concatenatedData.map((hospital) => hospital.state)
      ))
      .filter((state) => undefined !== state && '' !== state);
    // If the sorted column hasn't been set by the URL, use the most recent year
    const overrideSort = this.state.sortedColumn ?
      this.state.sortedColumn : null;

    const fullStateList = ['AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY']; // eslint-disable-line max-len

    const allStatesSorted = fullStateList.filter( (state) => allStates.includes(state)); // eslint-disable-line

    this.setState((prevState) => ({
      data: concatenatedData,
      hacYears,
      loading: false,
      sortedColumn: overrideSort,
      tableData: this.filterAndSort(
        concatenatedData,
        prevState.activePenalty,
        overrideSort,
        prevState.activeSearch,
        prevState.activeState,
        prevState.sortOrder
      ),
      readmissionYears,
      states: allStatesSorted,
    }));
  }
  /**
   * Shorthand to filters and sort table data
   * @param {Array} dataArray - Data to be filtered and sorted.
   * @param {string} activePenalty - Active penalization type.
   * @param {string|number} sortedColumn - Column being sorted.
   * @param {string} activeSearch - Search string.
   * @param {string} activeState - State by which to filter.
   * @param {string} sortOrder - 'ASC' or 'DESC'.
   */
  filterAndSort( // eslint-disable-line class-methods-use-this
    dataArray,
    activePenalty,
    sortedColumn,
    activeSearch,
    activeState,
    sortOrder
  ) {
    const filteredData = getFilteredData(
      dataArray,
      activeSearch,
      activeState
    );
    return sortRowsByField(
      filteredData,
      activePenalty,
      sortedColumn,
      sortOrder
    );
  }
  /**
   * Click listener for sorting table columns.
   * @param {number|string} column - The column for which to sort contributions.
   * @param {string} order - 'ASC' or 'DESC'.
   */
  handleTableSortClick(column, order) {
    this.setState((prevState) => ({
      tableData: sortRowsByField(
        prevState.tableData,
        prevState.activePenalty,
        column,
        order
      ),
      sortedColumn: column,
      sortOrder: order,
    }));
  }
  /**
   * @returns {object} Component markup.
   */
  render() {
    const {
      activePenalty,
      activeSearch,
      activeState,
      hacYears,
      loading,
      readmissionYears,
      sortOrder,
      sortedColumn,
      states,
      tableData,
    } = this.state;

    // Render filters and table after initial data load.
    if (loading) {
      return (
        <div className="hospitalPenalties">
          <div className="loading">
            <img src={loader} alt="" />
            <span>Loading</span>
          </div>
        </div>
      );
    }

    return (
      <div className="hospitalPenalties">
        <h2>Look Up Hospital Penalties</h2>
        <PenaltiesFilters
          activePenalty={activePenalty}
          activeSearch={activeSearch}
          activeState={activeState}
          onFilter={this.onFilter}
          onResetFilters={this.onResetFilters}
          states={states}
          years={'hac' === activePenalty ? hacYears : readmissionYears}
        />
        <PenaltiesTable
          activePenalty={activePenalty}
          data={tableData}
          handleTableSortClick={this.handleTableSortClick}
          loading={loading}
          onFilter={this.onFilter}
          sortOrder={sortOrder}
          sortedColumn={sortedColumn}
          years={'hac' === activePenalty ? hacYears : readmissionYears}
        />
        <div className="source">
          Source: KFF Health News analysis of hospital data from the Centers for Medicare
          & Medicaid Services
        </div>
      </div>
    );
  }
}

ReactDOM.render(React.createElement(HospitalPenalties, null), appElement);
